import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSettingsAccess } from '@/components/settings/useSettingsAccess';
import { useToast } from '@/contexts/ToastContext';
import { toastError } from '@/lib/toastErrors';
import { apiPost } from '@/lib/api';
import { useWorkerAction } from '@/hooks/useWorkerAction';
import { Button } from '@/components/ui/Button';
import { SettingsSection } from '@/components/settings/SettingsSection';

export function FiscalPeriodSection() {
  const { t } = useTranslation();
  const toast = useToast();
  const { orgId, currentPeriod, online, canEdit } = useSettingsAccess();
  const { ensureReady } = useWorkerAction('settings-period');
  const [closing, setClosing] = useState(false);

  async function closePeriod() {
    if (!orgId || !currentPeriod?.id || !online || !canEdit) return;
    if (!window.confirm(t('settings.closePeriodConfirm'))) return;
    if (!(await ensureReady())) return;
    setClosing(true);
    try {
      await apiPost('/v1/periods/close', { orgId, periodId: currentPeriod.id });
      toast.success(t('settings.periodClosed'), 'settings-period');
    } catch (e) {
      toastError(toast, e, 'settings-period');
    } finally {
      setClosing(false);
    }
  }

  return (
    <SettingsSection id="settings-fiscal-period" title={t('settings.fiscalPeriod')}>
      <p className="text-sm text-[var(--fv-text-muted)]">
        {currentPeriod?.name} —{' '}
        {t(`settings.periodStatus.${currentPeriod?.status ?? 'unknown'}`, {
          defaultValue: currentPeriod?.status
        })}
      </p>
      {currentPeriod?.status === 'open' && canEdit && (
        <Button
          variant="secondary"
          disabled={!online || closing}
          onClick={() => void closePeriod()}
        >
          {t('settings.closePeriod')}
        </Button>
      )}
    </SettingsSection>
  );
}
